import { useState, useMemo, useEffect } from 'react';
import { useModalContentManager } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { TextInput, Select, Button, Tooltip } from '@ui/kit';
import { useSubjects, levels } from '@utils/constants';
import { dialogCls, headingCls, inputWrapperCls, selectWrapperCls, selectDropdownCls, buttonContainerCls, errorEntryCls } from './settings-lightbox.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useUpdateCourseMutation } from '../api/mutation.update.js';

function SettingsLightbox(props) {
  const {
    onDismiss
  } = useModalContentManager();
  const user = useUserIdentityState(state => state.user);
  const subjects = useSubjects();
  const [temporaryCourse, callbacks] = useTemporaryCourse(props.course);
  const isTitleInvalid = temporaryCourse.title === "";
  const submitDisabled = isTitleInvalid;
  const errors = [isTitleInvalid && "Du mangler at udfylde en titel"].filter(Boolean);
  const {
    mutate
  } = useUpdateCourseMutation();
  const handleSubmit = async evt => {
    evt.preventDefault();
    if (submitDisabled || !user) {
      return;
    }

    // Don't bother awaiting the update course. The notification center will
    // notify users in case anything goes wrong.
    mutate({
      oldCourse: props.course,
      newCourse: temporaryCourse
    });
    onDismiss();
  };
  return /*#__PURE__*/jsxs("form", {
    className: dialogCls,
    noValidate: true,
    onSubmit: handleSubmit,
    children: [/*#__PURE__*/jsx("h1", {
      className: headingCls,
      children: "Indstillinger"
    }), /*#__PURE__*/jsxs("div", {
      className: inputWrapperCls,
      children: [/*#__PURE__*/jsx(TextInput, {
        label: "Titel",
        invalid: isTitleInvalid,
        value: temporaryCourse.title,
        onChange: callbacks.updateTitle,
        required: true,
        maxLength: 100
      }), /*#__PURE__*/jsxs("div", {
        className: selectWrapperCls,
        children: [/*#__PURE__*/jsx(Select, {
          label: "Niveau",
          dropdownClassName: selectDropdownCls,
          selectedValues: temporaryCourse.levels,
          options: levels,
          multiSelect: true,
          onChange: callbacks.updateLevels
        }), /*#__PURE__*/jsx(Select, {
          label: "Fag",
          dropdownClassName: selectDropdownCls,
          selectedValues: temporaryCourse.subjects,
          options: subjects,
          multiSelect: true,
          onChange: callbacks.updateSubjects
        })]
      })]
    }), /*#__PURE__*/jsxs("div", {
      className: buttonContainerCls,
      children: [/*#__PURE__*/jsx(Button, {
        theme: "secondary",
        type: "button",
        onClick: () => onDismiss(),
        children: "Annuller"
      }), /*#__PURE__*/jsx(Tooltip, {
        body: errors.map((error, index) => /*#__PURE__*/jsx("div", {
          className: errorEntryCls,
          children: error
        }, error || index)),
        ariaType: "description",
        disabled: !submitDisabled,
        placement: "top",
        children: ref => /*#__PURE__*/jsx(Button, {
          ref: ref,
          "aria-disabled": submitDisabled,
          type: "submit",
          theme: "primary",
          children: "Gem"
        })
      })]
    })]
  });
}

/**
 * Transforms a course into a temporary variant, allowing modification inside
 * the settings dialog which only should be applied in case the submit button
 * is pressed.
 */
function useTemporaryCourse(course) {
  const [tmpCourse, setCourse] = useState({
    ...course
  });
  const callbacks = useMemo(() => ({
    updateTitle: newTitle => {
      setCourse({
        ...tmpCourse,
        title: newTitle
      });
    },
    updateLevels: newLevels => {
      newLevels.sort((a, b) => levels.indexOf(a) - levels.indexOf(b));
      setCourse({
        ...tmpCourse,
        levels: newLevels
      });
    },
    updateSubjects: newSubjects => {
      newSubjects.sort((a, b) => a.localeCompare(b, "da-DK", {
        numeric: true
      }));
      setCourse({
        ...tmpCourse,
        subjects: newSubjects
      });
    }
  }), [tmpCourse]);
  useEffect(() => {
    setCourse(course);
  }, [course]);
  return [tmpCourse, callbacks];
}

export { SettingsLightbox };
//# sourceMappingURL=settings-lightbox.js.map
